const UMBRA_URL = import.meta.env.VITE_UMBRA_API_URL;
// Single source of truth for the /umbra/v2/chats base — both the messages
// endpoint and the Phase-4 sync chat-list endpoint resolve to it. Referenced
// (not duplicated) below so the two can't drift apart.
const UMBRA_CHATS_URL = `${UMBRA_URL}/umbra/v2/chats`;
export const ApiEndpoints = {
  // ---------------------
  // NEW UMBRA ENDPOINTS
  // ---------------------
  UMBRA_MESSAGES: UMBRA_CHATS_URL,
  // AUTH
  LOGIN: `${UMBRA_URL}/umbra/auth/login`,
  LOGOUT: `${UMBRA_URL}/umbra/auth/logout`,
  REGISTER: `${UMBRA_URL}/umbra/auth/register`,
  KDF_PARAMS: `${UMBRA_URL}/umbra/auth/kdf-params`,
  KDF_UPGRADE: `${UMBRA_URL}/umbra/auth/kdf-upgrade`,
  REGISTER_BATCH: "/register/users_basic_batch",
  SEND_VERIFICATION_CODE: `${UMBRA_URL}/umbra/auth/send-verification-code`,
  VERIFY_EMAIL: `${UMBRA_URL}/umbra/auth/verify-email`,
  GET_EMAIL: `${UMBRA_URL}/umbra/auth/email`,
  CHANGE_EMAIL: `${UMBRA_URL}/umbra/auth/change-email`,
  CONFIRM_EMAIL_CHANGE: `${UMBRA_URL}/umbra/auth/confirm-email-change`,
  FORGOT_USERNAME: `${UMBRA_URL}/umbra/auth/forgot-username`,
  REFRESH_TOKEN: `${UMBRA_URL}/umbra/auth/refresh`,
  // RESET PASSWORD
  USER_EXISTS: `${UMBRA_URL}/umbra/auth/exists`,
  ENCRYPTED_PASSWORD: `${UMBRA_URL}/umbra/auth/encrypted-password`,
  UPDATE_ENCRYPTED_DATA: `${UMBRA_URL}/umbra/auth/encrypted-data`,
  // OG-TAGS
  OG_TAGS: `${UMBRA_URL}/umbra/og-tags`,
  // CHATS
  CHAT: "api/chats",
  ENRICHED_CHATS: "api/enriched/chats",
  P2P_CHATS: "api/p2pchats",
  GROUP_CHATS: "api/chats/members",
  MUTED_CHATS: "cache/muted_chats",
  SEARCHABLE_GROUP_CHATS: "cache/group-search",
  MUTE_CHAT: "api/mute_chat",
  UNMUTE_CHAT: "api/unmute_chat",
  PIN_CHAT: "api/pin/chat",
  ARCHIVE_CHAT: "api/archive/chat",
  TRANSFER_OWNERSHIP: "api/members/ownership",
  PUBLIC_GROUP_JOIN_REQUEST: "api/group_requests/join",
  PUBLIC_GROUP_ACCEPT_REQUEST: "api/group_requests/accept",
  PUBLIC_GROUP_DENY_REQUEST: "api/group_requests/deny",
  PUBLIC_GROUP_USERS_REQUESTS: "api/group_requests/group",
  PUBLIC_GROUPS_USER_REQUESTS: "api/group_requests/user",
  // MESSAGES
  MESSAGES: "api/messages",
  RESET_COUNTERS: "api/reset_counters",
  PIN_MESSAGE: "api/pin/message",
  // ADMIN / SUPPORT PANEL (register service, nested at /register/)
  ADMIN_CAPABILITIES: "register/admin/capabilities",
  ADMIN_LOOKUP_USER: "register/admin/lookup_user",
  ADMIN_RESET_USER: "register/admin/reset_user",
  ADMIN_DELETE_USER: "register/admin/delete_user",
  ADMIN_AUDIT: "register/admin/audit",
  // CALLS
  CONFERENCE_TOKEN: "register/conference-token",
  // NEW Umbra minter (Phase-1 cutover, gated by featureFlags.useNewMinter).
  // Same `{ chat_id, chat_type }` contract as the legacy register endpoint.
  CONFERENCE_TOKEN_UMBRA: `${UMBRA_URL}/umbra/meet/conference-token`,
  // GUEST INVITES (Phase-2, ow2-chat-be #218).
  // Create: authenticated (owner/moderator of a GROUP). Body `{ chatId, ttlSeconds, maxJoins? }`
  //   → `{ inviteCode, expiresAt }` (raw code returned ONCE, never retrievable again).
  MEET_INVITES: `${UMBRA_URL}/umbra/meet/invites`,
  // Redeem: PUBLIC / unauthenticated. Body `{ code, displayName? }` → `{ token, room }`.
  // The raw code travels in the POST body (never the URL) to keep it out of access logs.
  MEET_INVITE_REDEEM: `${UMBRA_URL}/umbra/meet/invites/redeem`,
  // STANDALONE MEETINGS (Product B V0, ow2-chat-be#244). All AUTHENTICATED
  // (normal Bearer auth). No chat anywhere in this contract — the `meeting`
  // record is the authorization anchor and identity is server-derived.
  // Create: POST `{}` (body fields are ignored) → `{ meetingId, room }`;
  //   the caller becomes the owner (deterministic moderator).
  MEET_MEETINGS: `${UMBRA_URL}/umbra/meet/meetings`,
  // Token: POST exactly ONE of `{ meetingId }` XOR `{ alias }` →
  //   `{ token, room, subject }`. Owner mints a moderator token, any other
  //   authenticated user a member token; unknown/invalid → generic 404
  //   (no existence leak). NOT used for guests (guest flow is a follow-up).
  MEET_MEETINGS_TOKEN: `${UMBRA_URL}/umbra/meet/meetings/token`,
  // VANITY ALIAS (Product B vanity URL, meet-vanity-url plan). All AUTHENTICATED.
  // Claim: POST `{ alias }` → `{ meetingId, alias, room }` (room === meetingId).
  //   Premium fail-closed (403 AliasPremiumRequired); charset/reserved gates
  //   (400 AliasInvalid/AliasReserved); 409 AliasTaken / AliasAlreadyClaimed.
  //   Idempotent on re-claiming the SAME alias.
  MEET_MEETINGS_CLAIM_ALIAS: `${UMBRA_URL}/umbra/meet/meetings/claim-alias`,
  // Resolve: GET → `{ meetingId, alias, ownerUsername }` (generic 404). Used
  //   ONLY for the pre-join host-name display; joining mints straight from
  //   `{ alias }` via the token endpoint (no resolve needed to join).
  MEET_MEETINGS_RESOLVE: (alias: string) =>
    `${UMBRA_URL}/umbra/meet/meetings/resolve/${encodeURIComponent(alias)}`,
  // Mine: GET → the caller's personal meeting or a null sentinel. Backs the
  //   `/meet/vanity` manage page (show existing alias vs. claim form).
  MEET_MEETINGS_MINE: `${UMBRA_URL}/umbra/meet/meetings/mine`,
  // Meeting guest invites (Product B guest join). AUTHENTICATED, owner-only
  // (backend re-derives the caller and returns a generic 404 to non-owners —
  // no existence leak). Body `{ meetingId, ttlSeconds, maxJoins? }` →
  // `{ inviteCode, expiresAt }` (raw code returned ONCE). Redeem reuses the
  // existing public `MEET_INVITE_REDEEM` endpoint unchanged.
  MEET_MEETINGS_INVITES: `${UMBRA_URL}/umbra/meet/meetings/invites`,
  // RECORDINGS (Phase-5, ow2-chat-be #234/#237). MODERATOR-ONLY.
  // List: authenticated (owner/moderator of a GROUP). Cursor pagination via
  //   `?before=<ISO endedAt>&limit=<n>` (limit default 20, hard max 100) →
  //   `{ recordings, nextBefore }`. Non-moderator member → 403; non-member → 404.
  MEET_RECORDINGS_LIST: (chatId: string) =>
    `${UMBRA_URL}/umbra/meet/chats/${chatId}/recordings`,
  // Download: authenticated STREAMED mp4 (Bearer, Range/206). Owner/moderator only.
  //   The response is the RAW mp4 stream — NOT JSON, NOT a GCS redirect; the server
  //   never exposes the object path. A plain `<a href>` would omit the Bearer header,
  //   so fetch WITH auth → blob → save (see recordingService.downloadRecording).
  MEET_RECORDING_DOWNLOAD: (id: string) =>
    `${UMBRA_URL}/umbra/meet/recordings/${id}`,
  // USERS & MEMBERS
  USERS: "api/users",
  // Account deletion v2 (Umbra). Deliberately a SEPARATE constant: USERS is
  // shared by GET/PATCH and is relative to the legacy Rust base — it cannot
  // be repointed. Always deletes the token's own account; no body.
  DELETE_ACCOUNT: `${UMBRA_URL}/umbra/v2/users/me`,
  PREFIX_SEARCH: "cache/prefix-search",
  MEMBERS: "api/members",
  MEMBERS_BATCH: "api/members_batch",
  // EPOCHS (CSK Rotation)
  EPOCHS: "api/epochs",
  EPOCHS_LIST: "api/epochs/list",
  EPOCHS_PINNED: "api/epochs/pinned",
  EPOCHS_ROTATE_INIT: "api/epochs/rotate/init",
  EPOCHS_ROTATE_PAYLOADS: "api/epochs/rotate/payloads",
  EPOCHS_ROTATE_COMMIT: "api/epochs/rotate/commit",
  EPOCHS_ROTATE_ABORT: "api/epochs/rotate/abort",
  // MEDIA
  IMAGES: "file/images",
  ATTACHMENTS: "file/attachments",
  // CHUNKED UPLOAD
  CHUNKED_UPLOAD_START: `${UMBRA_URL}/umbra/attachments/upload/start`,
  CHUNKED_UPLOAD_PART: `${UMBRA_URL}/umbra/attachments/upload/part`,
  CHUNKED_UPLOAD_COMPLETE: `${UMBRA_URL}/umbra/attachments/upload/complete`,
  CHUNKED_UPLOAD_ABORT: `${UMBRA_URL}/umbra/attachments/upload/abort`,
  // CHUNKED DOWNLOAD (pre-assembly part for in-progress uploads)
  CHUNKED_DOWNLOAD_UPLOAD_PART: `${UMBRA_URL}/umbra/attachments/download/upload-part`,
  // FEEDBACK
  FEEDBACK: "feedback/reports",
  // STRIPE
  STRIPE_CHECKOUT: `${UMBRA_URL}/umbra/stripe/checkout-session`,
  STRIPE_PORTAL: `${UMBRA_URL}/umbra/stripe/portal-session`,
  STRIPE_SUBSCRIPTION: `${UMBRA_URL}/umbra/stripe/subscription`,
  STRIPE_INVOICES: `${UMBRA_URL}/umbra/stripe/invoices`,
  STRIPE_SESSION: `${UMBRA_URL}/umbra/stripe/session`,
  STRIPE_CREDIT_BALANCE: `${UMBRA_URL}/umbra/stripe/credit-balance`,
  STRIPE_CREDIT_CHECKOUT: `${UMBRA_URL}/umbra/stripe/credit-checkout`,
  STRIPE_CREDIT_SESSION: `${UMBRA_URL}/umbra/stripe/credit-session`,
  // TERMS & CONDITIONS
  TERMS_CURRENT: `${UMBRA_URL}/umbra/terms-and-conditions/current`,
  TERMS_ACCEPT: `${UMBRA_URL}/umbra/terms-and-conditions/accept`,
  // SYNC (Phase 4)
  SYNC_CHAT_LIST: UMBRA_CHATS_URL,
  SYNC_DELTA: `${UMBRA_URL}/umbra/v2/sync/chats`,
  SYNC_EVENTS: `${UMBRA_URL}/umbra/v2/sync/events`,
  // TELEMETRY — authenticated in-house sync-telemetry ingest (NO-PII operational
  // metrics). Replaces the previous PUBLIC + UNAUTHENTICATED feedback channel.
  TELEMETRY: `${UMBRA_URL}/umbra/telemetry`,
  // BFN CALCULATOR — entitlement-gated token exchange (Phase 3). Mints a
  // short-lived BFN token from the OA bearer; 403 when the user isn't entitled.
  BFN_TOKEN: `${UMBRA_URL}/umbra/bfn/token`,
} as const;
